import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["options"];
import { useMemo } from 'react';
import I18n from 'I18n';
import { useHasScope } from 'automation-ui-core/hooks/authHooks';
import { useGetInjectedFilterFamilies } from 'automation-ui-enrollment/hooks/useEventEnrollmentStrategy';
import { WEBHOOK_EVENTS_GROUP } from 'automation-ui-enrollment/constants/TriggerGroups';
import { TriggerCategories } from 'automation-ui-enrollment/constants/TriggerCategories';
import sortByText from 'automation-ui-enrollment/utils/sortByText';
import { getListTriggerOption, getWebhookTriggerOption } from 'automation-ui-canvas/create/constants/TriggerOptions';
import { CONTACTS_LISTS_ACCESS } from 'automation-ui-enrollment/constants/Scopes';
import useWorkflowTypeOptions from 'automation-ui-enrollment/components/workflowType/hooks/useWorkflowTypeOptions';
import { useGetHasListsToolAccess } from 'automation-ui-enrollment/hooks/useHasListsToolAccess';
import { isListMembershipEventName } from 'automation-ui-enrollment/utils/listMembershipUtils';
import { WEBHOOK_TRIGGERS_V2_OBJECT_TYPES } from 'automation-ui-enrollment/hooks/useHasWebhookTriggersAccess';
function resolveObjectTypeIdsForEventTrigger({
  workflowTypeIds,
  triggerOption,
  hasListsToolAccess
}) {
  let objectTypeIds = workflowTypeIds;
  const {
    supportedObjectTypeIds,
    blockedObjectTypeIds = []
  } = triggerOption;
  if (supportedObjectTypeIds.length > 0 || blockedObjectTypeIds.length > 0) {
    objectTypeIds = objectTypeIds.filter(objectTypeId => {
      const isIncluded = supportedObjectTypeIds.length === 0 || supportedObjectTypeIds.includes(objectTypeId);
      const isExcluded = blockedObjectTypeIds.includes(objectTypeId);
      return isIncluded && !isExcluded;
    });
  } else if (isListMembershipEventName(triggerOption.fullyQualifiedName)) {
    objectTypeIds = objectTypeIds.filter(objectTypeId => hasListsToolAccess(objectTypeId));
  }
  return new Set(objectTypeIds);
}
export function useTriggerOptions() {
  const hasListsAccessScope = useHasScope(CONTACTS_LISTS_ACCESS);
  const hasListsToolAccess = useGetHasListsToolAccess();
  const {
    injectedFilterFamilies,
    injectedFilterFamiliesLoading
  } = useGetInjectedFilterFamilies({
    strategyOptions: {
      showListMembershipFilterFamily: hasListsAccessScope
    }
  });
  const {
    options: workflowTypeOptions,
    loading: workflowTypesLoading
  } = useWorkflowTypeOptions();
  const {
    triggerOptions,
    webhookTriggerOptions
  } = useMemo(() => {
    var _categories$get$get$o, _categories$get;
    const categories = new Map();
    function insertTriggerOption(option, categoryId, groupId, groupLabel) {
      let category = categories.get(categoryId);
      if (!category) {
        category = new Map();
        categories.set(categoryId, category);
      }
      const group = category.get(groupId);
      if (group) {
        group.options.push(option);
      } else {
        category.set(groupId, {
          text: groupLabel !== null && groupLabel !== void 0 ? groupLabel : '',
          groupId,
          options: [option]
        });
      }
    }
    const workflowTypeIds = workflowTypeOptions.map(({
      value
    }) => value);
    injectedFilterFamilies.forEach(option => {
      var _option$groupId;
      insertTriggerOption(Object.assign({}, option, {
        type: 'EVENT',
        objectTypeIds: resolveObjectTypeIdsForEventTrigger({
          workflowTypeIds,
          triggerOption: option,
          hasListsToolAccess
        })
      }), option.categoryId, (_option$groupId = option.groupId) !== null && _option$groupId !== void 0 ? _option$groupId : '', option.groupLabel);
    });
    insertTriggerOption(Object.assign({}, getListTriggerOption(), {
      objectTypeIds: new Set(workflowTypeIds)
    }), 'data', '');
    insertTriggerOption(Object.assign({}, getWebhookTriggerOption(), {
      objectTypeIds: new Set(WEBHOOK_TRIGGERS_V2_OBJECT_TYPES)
    }), 'advanced', '');
    const webhookOptions = sortByText((_categories$get$get$o = (_categories$get = categories.get('advanced')) === null || _categories$get === void 0 || (_categories$get = _categories$get.get(WEBHOOK_EVENTS_GROUP)) === null || _categories$get === void 0 ? void 0 : _categories$get.options) !== null && _categories$get$get$o !== void 0 ? _categories$get$get$o : []);
    const allOptions = sortByText(Array.from(categories.entries()).map(([categoryId, category]) => ({
      text: I18n.text(TriggerCategories[categoryId].categoryTitleKey),
      categoryId,
      options: sortByText(Array.from(category.values()).map(_ref => {
        let {
            options
          } = _ref,
          rest = _objectWithoutPropertiesLoose(_ref, _excluded);
        return Object.assign({
          options: sortByText(options)
        }, rest);
      })),
      sortOrder: TriggerCategories[categoryId].sortOrder
    })));
    return {
      triggerOptions: allOptions,
      webhookTriggerOptions: webhookOptions
    };
  }, [injectedFilterFamilies, workflowTypeOptions, hasListsToolAccess]);
  return {
    loading: injectedFilterFamiliesLoading || workflowTypesLoading,
    triggerOptions,
    webhookTriggerOptions
  };
}